import React, {useMemo} from 'react';

import {ChangeLogInitiatorCell, ChangeLogTimestampCell, ChangeLogTypeCell} from 'src/components/change-log';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';

import {
    Cell,
    type CellOptions,
    Column, DataTable,
    HeaderCellOptions,
    type RowsParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';


import {AuditLogPagination} from './audit-log-pagination';
import {ActionDescriptionCell} from './cells';
import {COLUMNS, COLUMNS_DATA, HEADERS} from './constants';

import type {LogHits} from 'src/openapi/admin/models/LogHits';
import type {Pagination} from 'src/openapi/admin/models/Pagination';

type Props = {
    rows: LogHits[],
    totalCount: number,
    pagination: Pagination,
    actionTypeDictionary: DictionaryItem[],
    onLogClick: (logId: string) => void,
};

export const UserLogTable = (
    {rows, totalCount, pagination, actionTypeDictionary, onLogClick}: Props,
) => {
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const renderedRows = useMemo(() => (rows
        .map(log => ({
            id: log.recordId,
            columnValues: {
                actionName: log.actionName,
                actionDescription: log.actionDescription,
                initiator: {
                    user: log.initiatorUser,
                    system: log.initiatorSystem,
                    process: log.initiatorSystemProcess,
                },
                timestamp: log.timestamp,
                reason: log.reason,
            },
        }))
    ), [rows]);

    const maxRowNumber = getPaginatedRowNumber({...pagination, index: renderedRows?.length});

    return (
        <DataTable
            id="user-audit-log-table"
            data={{
                rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                    ...column,
                    width: getColumnWidthByColumnId(column.columnId),
                })),
            }}
            getHeaderTopOffset={() => 157}
            onStopResize={onStopResize}
            headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                switch (column.columnId) {
                    case COLUMNS.ACTION_NAME:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <MainColumnHeader
                                    selection={<div/>}
                                    maxRowNumber={maxRowNumber}>
                                    <strong>{HEADERS[column.columnId]}</strong>
                                </MainColumnHeader>
                            </Cell>
                        );
                    default:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <TableHeader indent="none" className="search-header">
                                    <strong>{HEADERS[column.columnId]}</strong>
                                </TableHeader>
                            </Cell>
                        );
                }
            }}
            renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                const {rowIndex, column: {columnId}} = rowsParams;
                const value = rowsParams.row.columnValues[columnId];
                const column = rowsParams.column;
                const rowId = rowsParams.row.id as string;
                const cellKey = `${rowId}-${columnId}`;
                const rowNumber = getPaginatedRowNumber({
                    ...pagination,
                    index: rowIndex + 1,
                });

                switch (columnId) {
                    case COLUMNS.ACTION_NAME:
                        return (
                            <MainColumnCell
                                rowId={+rowId}
                                key={cellKey}
                                selection={<div/>}
                                rowNumber={rowNumber}
                                maxRowNumber={maxRowNumber}
                                cellOptions={{...cellOptions, column}}>
                                <ChangeLogTypeCell
                                    logId={rowId}
                                    actionTypeCode={value as string}
                                    actionTypeDictionary={actionTypeDictionary}
                                    onLogClick={onLogClick}
                                />
                            </MainColumnCell>
                        );
                    case COLUMNS.TIMESTAMP:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                {value
                                    ? <ChangeLogTimestampCell timestamp={value as string}/>
                                    : '--'}
                            </Cell>
                        );
                    case COLUMNS.INITIATOR:
                        const initiator = value as React.ComponentProps<typeof ChangeLogInitiatorCell>;
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <ChangeLogInitiatorCell
                                    user={initiator.user}
                                    process={initiator.process}
                                    system={initiator.system}
                                    place="User Audit Log Preview"
                                />
                            </Cell>
                        );
                    case COLUMNS.ACTION_DESCRIPTION:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                {value
                                    ? <ActionDescriptionCell description={value as string}/>
                                    : '--'}
                            </Cell>
                        );
                    default:
                        const displayValue = value ? String(value) : '--';
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <EllipsisTextHint title={displayValue}>
                                    {displayValue}
                                </EllipsisTextHint>
                            </Cell>
                        );
                }
            }}
            paginationStripe={<AuditLogPagination totalCount={totalCount}/>}
        />
    );
};
